/**
 * ReconcileTab — Batch #14.312 : Vérificateur auxiliaires ↔ grand livre (tuile « Concordance GL », deux portées).
 * Comptes clients / fournisseurs / banque : solde du grand livre vs pièces reportées, chaque écart expliqué, corrections tracées (motif obligatoire).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, History, Info, Landmark, Lightbulb, Loader2, RefreshCw, Send, ShieldAlert, Wrench } from "lucide-react";
import { toast } from "@/lib/toast";
import { api } from "@/api";
import { Btn, Empty, Field, Modal, Pill, errMsg, inputCls, money, today, useGlBase } from "../comptabilite/_shared";

const SEV = {
  error: { label: "Anomalie", cls: "bg-rose-100 text-rose-800 border-rose-200", icon: ShieldAlert },
  warning: { label: "À vérifier", cls: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle },
  info: { label: "Information", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: Info },
};
const FIX_LABEL = {
  recreate_entry: "Recréer l'écriture", void_entry: "Annuler l'écriture", cancel_payment: "Annuler le paiement",
  cancel_payment_and_void_reversal: "Annuler paiement + contre-passation", force_unpost: "Annuler le report", post_pending: "Reporter au grand livre",
};
const FIX_HELP = {
  recreate_entry: "L'écriture attendue est recréée (non reportée) à partir de la pièce ; l'ancienne, si elle existe encore, est annulée avec le motif. Terminez par « Reporter au grand livre ».",
  void_entry: "L'écriture est annulée au grand livre (conservée dans le journal, statut « annulée », avec votre motif). Les soldes ne la comptent plus.",
  cancel_payment: "Le paiement est retiré de l'auxiliaire et son écriture annulée : la facture redevient à payer / à recevoir.",
  cancel_payment_and_void_reversal: "Le paiement est retiré (son écriture d'origine annulée) ET la contre-passation manuelle est annulée — sinon l'effet serait compté deux fois. La facture redevient à payer / à recevoir.",
  force_unpost: "L'écriture de la pièce est annulée au grand livre (statut « annulée », motif conservé) et la pièce redevient brouillon ; cochez « Supprimer » pour la retirer complètement. Possible seulement en période non verrouillée et sans montant appliqué.",
  post_pending: "Les écritures en brouillon des pièces reportées sont numérotées et reportées au grand livre dans un lot de report (rapport PDF dans Journal général › Lots de report). Les écarts « écritures à reporter » disparaissent.",
};
const SECTION_LABEL = { ar: "Clients", ap: "Fournisseurs", bank: "Banque" };

function BankCard({ s, pathFor }) {
  const anomalies = Math.abs(s.anomalies_total || 0) > 0.004 || s.nb_errors > 0;
  const rec = s.bank_rec || {};
  const recOk = rec.available && rec.difference != null && Math.abs(rec.difference) < 0.005;
  return (
    <div className={`rounded-xl border p-3 ${anomalies ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-white"}`} data-testid={`reconcile-section-bank-${s.account.number}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-bold text-slate-700 inline-flex items-center gap-1"><Landmark className="w-3.5 h-3.5 text-teal-700" /> {s.label}</div>
        <span className="text-[10px] font-mono text-slate-500">{s.account.number} — {s.account.name}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
        <div><div className="text-slate-500">Grand livre</div><div className="font-mono font-semibold" data-testid="reconcile-gl">{money(s.gl_balance)}</div></div>
        <div><div className="text-slate-500">Encaissements / décaissements</div><div className="font-mono font-semibold" data-testid="reconcile-sub">{money(s.sub_balance)}</div></div>
      </div>
      {(s.breakdown || []).length > 0 && (
        <div className="mt-2 space-y-0.5" data-testid="reconcile-bank-breakdown">
          {s.breakdown.map((b) => (
            <div key={b.type} className="flex items-center justify-between text-[10px] text-slate-600" data-testid={`reconcile-bank-breakdown-${b.type}`}>
              <span>{b.label} <span className="text-slate-400">({b.count})</span></span><span className="font-mono">{money(b.amount)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-1.5 text-[10px]">
        {anomalies ? <span className="text-rose-700 inline-flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> {s.nb_errors} anomalie{s.nb_errors > 1 ? "s" : ""} ({money(s.anomalies_total)}) — voir le tableau</span>
          : Math.abs(s.unexplained) < 0.005 ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Aucune anomalie — pas d'auxiliaire pour l'encaisse : les autres mouvements sont normaux</span>
            : <span className="text-rose-700">Écart inexpliqué : {money(s.unexplained)}</span>}
      </div>
      {s.flagged === false && <p className="text-[10px] text-amber-700 mt-1" data-testid="reconcile-bank-unflagged">Compte utilisé par des paiements mais non coché « à rapprocher » (Plan comptable).</p>}
      {rec.available && (
        <div className={`mt-2 rounded-lg border px-2 py-1.5 text-[10px] ${recOk ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-50 text-slate-700"}`} data-testid="reconcile-bank-rec">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold">Rapprochement bancaire</span>
            <Link to={`${pathFor ? pathFor("banque") : "banque"}?account=${s.account.id}`} className="underline" data-testid="reconcile-bank-rec-link">Ouvrir</Link>
          </div>
          {rec.bank_balance == null ? <div>Aucun relevé importé — importez un relevé pour contrôler ce compte.</div> : (
            <div>Solde relevé {money(rec.bank_balance)} ({rec.bank_balance_date}) · {rec.unmatched_lines} ligne{rec.unmatched_lines > 1 ? "s" : ""} de relevé et {rec.outstanding_gl} écriture{rec.outstanding_gl > 1 ? "s" : ""} à apparier{rec.difference != null ? ` · différence ${money(rec.difference)}` : ""}</div>
          )}
        </div>
      )}
    </div>
  );
}

function SectionCard({ s, pathFor }) {
  if (s.role === "bank" && s.account) return <BankCard s={s} pathFor={pathFor} />;
  const ok = Math.abs(s.difference) < 0.005;
  return (
    <div className={`rounded-xl border p-3 ${s.missing_account ? "border-dashed border-slate-300 bg-slate-50" : ok ? "border-emerald-200 bg-emerald-50/40" : "border-rose-200 bg-rose-50/40"}`} data-testid={`reconcile-section-${s.role}${s.account ? `-${s.account.number}` : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-bold text-slate-700">{s.label}</div>
        {s.account && <span className="text-[10px] font-mono text-slate-500">{s.account.number} — {s.account.name}</span>}
      </div>
      {s.missing_account ? <p className="text-[11px] text-slate-500 mt-2">Aucun compte attribué à ce rôle (Plan comptable › Rôles & réglages).</p> : (
        <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
          <div><div className="text-slate-500">Grand livre</div><div className="font-mono font-semibold" data-testid="reconcile-gl">{money(s.gl_balance)}</div></div>
          <div><div className="text-slate-500">Auxiliaire</div><div className="font-mono font-semibold" data-testid="reconcile-sub">{money(s.sub_balance)}</div></div>
          <div><div className="text-slate-500">Écart</div><div className={`font-mono font-bold ${ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="reconcile-diff">{ok ? "0,00 $" : money(s.difference)}</div></div>
        </div>
      )}
      {!s.missing_account && !ok && (
        <div className="mt-1.5 text-[10px]">
          {Math.abs(s.unexplained) < 0.005 ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Écart expliqué à 100 % par {s.nb_items} élément{s.nb_items > 1 ? "s" : ""}{s.nb_errors ? ` (${s.nb_errors} anomalie${s.nb_errors > 1 ? "s" : ""})` : ""}</span>
            : <span className="text-rose-700">Écart inexpliqué : {money(s.unexplained)}</span>}
        </div>
      )}
    </div>
  );
}

function ItemRow({ it, onFix }) {
  const sev = SEV[it.severity];
  const ref = it.doc || {};
  return (
    <tr className="align-top" data-testid={`reconcile-item-${it.type}`}>
      <td className="px-2 py-2"><Pill cls={sev.cls}>{sev.label}</Pill><div className="text-[10px] text-slate-500 mt-1">{SECTION_LABEL[it.section]}</div></td>
      <td className="px-2 py-2 text-xs"><div className="font-semibold text-slate-800">{it.type_label}</div><div className="text-[11px] text-slate-600 mt-0.5">{it.explanation}</div></td>
      <td className="px-2 py-2 text-xs">
        {it.doc && <div><span className="font-medium">{ref.label} {ref.number}</span><div className="text-[10px] text-slate-500">{ref.date} · {ref.party_name} · {money(ref.amount)}</div></div>}
        {it.entry && <div className="text-[10px] text-slate-500 mt-0.5">Écriture n° {it.entry.no ?? "—"} · {it.entry.journal} · {it.entry.date}{it.entry.status === "void" ? " · annulée" : it.entry.status === "draft" ? " · non reportée" : ""}</div>}
        {it.reversed_entry && <div className="text-[10px] text-slate-500">Contre-passe n° {it.reversed_entry.no}</div>}
      </td>
      <td className="px-2 py-2 text-xs text-right font-mono whitespace-nowrap" data-testid="reconcile-item-effect">{money(it.effect_display)}</td>
      <td className="px-2 py-2">
        <div className="flex flex-col gap-1 items-stretch">
          {(it.fixes || []).map((f) => (
            <button key={f} onClick={() => onFix(it, f)} className="text-[11px] px-2 py-1 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 inline-flex items-center gap-1 whitespace-nowrap" data-testid={`reconcile-fix-${f}`}>
              <Wrench className="w-3 h-3" /> {FIX_LABEL[f]}{f === "void_entry" && it.entry?.no ? ` n° ${it.entry.no}` : ""}
            </button>
          ))}
        </div>
      </td>
    </tr>
  );
}

function FixModal({ fix, base, onClose, onDone }) {
  const { item, action, proposal } = fix;
  const [reason, setReason] = useState(action === "post_pending" ? "Report au grand livre des écritures en attente (Concordance)" : "");
  const [del, setDel] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 3) { toast.error("Indiquez un motif (traçabilité)"); return; }
    setBusy(true);
    try {
      const body = { action, reason: reason.trim(), kind: item?.doc?.kind, id: item?.doc?.id, entry_id: item?.entry?.id, entry_ids: proposal?.entry_ids || [], delete: del };
      const r = await api.post(`${base}/reconcile/fix`, body);
      toast.success(r.data.message || "Correction appliquée");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={FIX_LABEL[action]} sub={item?.type_label || proposal?.title} onClose={onClose} testId="reconcile-fix-modal"
      footer={<><Btn tone="slate" onClick={onClose} testId="reconcile-fix-cancel">Annuler</Btn><Btn tone={action === "post_pending" ? "emerald" : "rose"} icon={action === "post_pending" ? Send : Wrench} onClick={submit} busy={busy} testId="reconcile-fix-confirm">{action === "post_pending" ? `Reporter ${proposal?.count || ""} écriture${(proposal?.count || 0) > 1 ? "s" : ""}` : "Confirmer la correction"}</Btn></>}>
      <div className="space-y-3 text-sm">
        {item && (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700">
            {item.doc && <div><b>{item.doc.label} {item.doc.number}</b> · {item.doc.date} · {item.doc.party_name} · {money(item.doc.amount)}</div>}
            {item.entry && <div>Écriture n° {item.entry.no ?? "—"} · {item.entry.journal} · {item.entry.date} — {item.entry.memo}</div>}
            <div className="mt-1 text-slate-600">{item.explanation}</div>
          </div>
        )}
        {proposal && <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700" data-testid="reconcile-fix-proposal">{proposal.text}</div>}
        <p className="text-xs text-slate-700" data-testid="reconcile-fix-help">{FIX_HELP[action]}</p>
        {action === "force_unpost" && (
          <label className="inline-flex items-center gap-2 text-xs"><input type="checkbox" checked={del} onChange={(e) => setDel(e.target.checked)} data-testid="reconcile-fix-delete" /> Supprimer la pièce après l'annulation du report</label>
        )}
        <Field label="Motif (obligatoire — conservé dans le journal des corrections et sur l'écriture annulée)">
          <textarea className={inputCls} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. : paiement jamais effectué, note créée en double…" data-testid="reconcile-fix-reason" />
        </Field>
      </div>
    </Modal>
  );
}

function Proposals({ proposals, onPost }) {
  if (!proposals?.length) return null;
  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3 space-y-2" data-testid="reconcile-proposals">
      <div className="text-xs font-bold text-sky-900 inline-flex items-center gap-1.5"><Lightbulb className="w-4 h-4" /> Propositions de correction</div>
      {proposals.map((p, i) => (
        <div key={i} className="flex flex-wrap items-start gap-2 justify-between rounded-lg bg-white border border-sky-100 p-2.5" data-testid={`reconcile-proposal-${p.action}`}>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-slate-800 inline-flex items-center gap-1.5">{p.severity === "error" ? <ShieldAlert className="w-3.5 h-3.5 text-rose-600" /> : <Info className="w-3.5 h-3.5 text-sky-600" />} {p.title}</div>
            <div className="text-[11px] text-slate-600 mt-0.5">{p.text}</div>
          </div>
          {p.action === "post_pending" && <Btn icon={Send} onClick={() => onPost(p)} testId="reconcile-proposal-post-btn">Reporter au grand livre maintenant</Btn>}
          {p.action !== "post_pending" && <a href="#reconcile-items" className="text-[11px] font-semibold text-sky-700 underline whitespace-nowrap" data-testid="reconcile-proposal-goto">Voir dans le tableau ↓</a>}
        </div>
      ))}
    </div>
  );
}

function LogTable({ items }) {
  if (!items) return <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></div>;
  if (!items.length) return <Empty icon={History} text="Aucune correction effectuée pour l'instant." testId="reconcile-log-empty" />;
  return (
    <div className="overflow-x-auto -mx-4">
      <table className="w-full text-xs" data-testid="reconcile-log">
        <thead className="bg-slate-50 border-y border-slate-200"><tr><th className="px-2 py-2 text-left">Date</th><th className="px-2 py-2 text-left">Par</th><th className="px-2 py-2 text-left">Correction</th><th className="px-2 py-2 text-left">Cible</th><th className="px-2 py-2 text-left">Motif</th><th className="px-2 py-2 text-left">Avant → après</th></tr></thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((l) => (
            <tr key={l.id} data-testid="reconcile-log-row">
              <td className="px-2 py-2 whitespace-nowrap">{String(l.at).slice(0, 16).replace("T", " ")}</td>
              <td className="px-2 py-2">{l.by}</td>
              <td className="px-2 py-2 font-medium">{FIX_LABEL[l.action] || l.action}</td>
              <td className="px-2 py-2">{l.target?.kind ? `${l.target.kind} ${l.target.number || ""}` : l.target?.no ? `Écriture n° ${l.target.no}` : "—"}</td>
              <td className="px-2 py-2 text-slate-700">{l.reason}</td>
              <td className="px-2 py-2 font-mono text-[10px] text-slate-500 max-w-[320px] truncate" title={`${JSON.stringify(l.before)} → ${JSON.stringify(l.after)}`}>{JSON.stringify(l.after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ReconcileTab({ refresh, pathFor }) {
  const base = useGlBase();
  const [asOf, setAsOf] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [view, setView] = useState("ecarts");
  const [log, setLog] = useState(null);
  const [fix, setFix] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`${base}/reconcile`, { params: { as_of: asOf } }); setData(r.data); }
    catch (e) { toast.error(errMsg(e, "Erreur de vérification")); }
    finally { setLoading(false); }
  }, [base, asOf]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (view === "journal") api.get(`${base}/reconcile/log`).then((r) => setLog(r.data.items || [])).catch(() => setLog([])); }, [view, base, data]);

  const items = (data?.items || []).filter((i) => showInfo || i.severity !== "info");
  const nbInfo = (data?.items || []).filter((i) => i.severity === "info").length;
  return (
    <div className="space-y-4" data-testid="reconcile-tab">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-800">Concordance auxiliaires ↔ grand livre</h3>
          <p className="text-[11px] text-slate-500">Comptes clients et fournisseurs : les pièces reportées (ventes, achats, notes, paiements) doivent égaler le compte de contrôle. Banque : pas d'auxiliaire — on vérifie seulement que chaque encaissement / décaissement a bien son écriture ; le contrôle de l'encaisse se fait dans le rapprochement bancaire. Les corrections exigent un motif et sont journalisées.</p>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-[11px] text-slate-500">Au<input type="date" className={`${inputCls} py-1 ml-2 inline-block w-auto`} value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="reconcile-as-of" /></label>
          <Btn tone="slate" icon={RefreshCw} onClick={load} busy={loading} testId="reconcile-refresh">Vérifier</Btn>
        </div>
      </div>
      {data?.locked_until && <p className="text-[11px] text-amber-700" data-testid="reconcile-locked">Grand livre verrouillé jusqu'au {data.locked_until} — les corrections touchant cette période sont refusées ; déverrouillez d'abord (Plan comptable › Rôles & réglages).</p>}
      {data && <Proposals proposals={data.proposals} onPost={(p) => setFix({ item: null, action: "post_pending", proposal: p })} />}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="reconcile-sections">
          {data.sections.map((s) => <SectionCard key={`${s.role}-${s.account?.id || "none"}`} s={s} pathFor={pathFor} />)}
        </div>
      )}
      <div className="flex gap-1 border-b" id="reconcile-items">
        {[["ecarts", `Écarts${data ? ` (${data.summary.errors} anomalie${data.summary.errors > 1 ? "s" : ""} · ${data.summary.warnings} à vérifier)` : ""}`], ["journal", "Journal des corrections"]].map(([id, label]) => (
          <button key={id} onClick={() => setView(id)} className={`px-3 py-2 text-xs border-b-2 ${view === id ? "border-emerald-600 text-emerald-700 font-semibold" : "border-transparent text-slate-500"}`} data-testid={`reconcile-view-${id}`}>{label}</button>
        ))}
      </div>
      {view === "journal" ? <LogTable items={log} /> : loading && !data ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-slate-400" /></div> : (
        <>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-slate-500">{items.length} élément{items.length > 1 ? "s" : ""} affiché{items.length > 1 ? "s" : ""}</span>
            <label className="inline-flex items-center gap-2 text-[11px] text-slate-600"><input type="checkbox" checked={showInfo} onChange={(e) => setShowInfo(e.target.checked)} data-testid="reconcile-show-info" /> Afficher les éléments normaux ({nbInfo} : écritures à reporter, soldes d'ouverture, notes non appliquées…)</label>
          </div>
          {items.length === 0 ? <Empty icon={CheckCircle2} text={data?.summary.errors === 0 ? "Aucune anomalie : l'auxiliaire et le grand livre concordent." : "Rien à afficher avec ce filtre."} testId="reconcile-empty" /> : (
            <div className="overflow-x-auto -mx-4">
              <table className="w-full text-xs" data-testid="reconcile-items">
                <thead className="bg-slate-50 border-y border-slate-200"><tr><th className="px-2 py-2 text-left">Gravité</th><th className="px-2 py-2 text-left">Constat</th><th className="px-2 py-2 text-left">Pièce / écriture</th><th className="px-2 py-2 text-right">Effet GL − aux.</th><th className="px-2 py-2 text-left">Correction</th></tr></thead>
                <tbody className="divide-y divide-slate-100">{items.map((it) => <ItemRow key={it.id} it={it} onFix={(item, action) => setFix({ item, action })} />)}</tbody>
              </table>
            </div>
          )}
        </>
      )}
      {fix && <FixModal fix={fix} base={base} onClose={() => setFix(null)} onDone={() => { setFix(null); load(); refresh?.(); }} />}
    </div>
  );
}
